import React, { Component } from 'react';
import { connect } from 'react-redux';
import { pending, rejected, done } from 'redux-saga-thunk';
import { withRouter } from 'react-router';
import { compose } from 'redux';
import get from 'lodash/get';
import { reduxForm } from 'redux-form';
import { notification } from 'antd';
import { fromEntities, fromResource } from '../store/selectors';
import AudienceTab from '../components/molecules/AudienceTab';
import { createValidator, required } from '../services/validation';
import {
  resourceCreateRequest,
  resourceDeleteRequest,
  resourceDetailReadRequest,
} from '../store/resource/actions';
import { modalHide, modalShow } from '../store/modal/actions';
import { isOpen } from '../store/modal/selectors';
import { startPollingForQueryStatus, stopPollingForQueryStatus } from '../store/audiences/actions';

class AudienceTabContainer extends Component {
  state = {
    showCreate: false,
  };

  componentDidMount() {
    Promise.all([this.props.getAudience(), this.props.verify()]).catch(e => {
      notification.error({
        message: 'Error',
        description: typeof e === 'string' ? e : 'Could not retrieve audience.',
        duration: 0,
      });
    });
  }

  componentDidUpdate(prevProps) {
    // Have a uuid when the audiences query is finished
    if (prevProps.uuid !== this.props.uuid) {
      // Run the polling
      this.props.pollForQueryStatusStart(this.props.uuid);
    }

    // If the ad is setup for audience targeting, but no targeting info can be found
    if (
      get(this.props, 'verifyResult.id') &&
      !get(prevProps, 'campaign.name') &&
      get(this.props, 'campaign.name') &&
      !this.props.count
    ) {
      notification.error({
        message: 'Error',
        description: `${this.props.campaign.name} might not be delivering because of an audience targeting issue, please alert the product/tech team.`,
        duration: 0,
      });
    }

    // Finished retrieving all devices
    if (
      this.props.count &&
      prevProps.count < this.props.deviceCount &&
      (this.props.deviceCount === this.props.count || this.props.secondsAge >= 3600)
    ) {
      // Stop the polling
      this.props.pollForQueryStatusStop(this.props.uuid);
    }

    // Stop polling when deletion is complete
    if (
      this.props.doneDeleteAudiencesQuery &&
      prevProps.count &&
      this.props.deviceCount &&
      !this.props.count
    ) {
      // Stop the polling
      this.props.pollForQueryStatusStop(this.props.uuid);
    }
  }

  toggleCreate = () => this.setState({ showCreate: !this.state.showCreate });

  deleteAll = () => {
    const { uuid, deleteAll, pollForQueryStatusStart } = this.props;

    deleteAll()
      .then(() => {
        notification.success({
          message: 'Success',
          description: 'Removing devices...',
          duration: 4,
        });
        pollForQueryStatusStart(uuid);
      })
      .catch(e => {
        notification.error({
          message: 'Error',
          description: typeof e === 'string' ? e : 'Could not retrieve audience.',
          duration: 0,
        });
      });
  };

  render() {
    return (
      <AudienceTab
        {...this.props}
        toggleCreate={this.toggleCreate}
        showCreate={this.state.showCreate || this.props.deviceCount}
        deleteAll={this.deleteAll}
      />
    );
  }
}

const mapStateToProps = (
  state,
  {
    match: {
      params: { lineItemId, campaignId },
    },
  }
) => {
  const query = fromEntities.getDetail(state, 'advertiserAdAudiences', lineItemId);
  const sql = get(query, 'metadata.query');
  const useStandardSql = get(query, 'metadata.useStandardSql');
  const secondsAge = get(query, 'metadata.secondsAge', 3601.0);

  return {
    campaign: fromEntities.getDetail(state, 'advertiserCampaign', campaignId),
    query: sql,
    verifyResult: fromEntities.getDetail(state, 'advertiserAdAudiencesVerify', lineItemId),
    uuid: query.uuid || null,
    visible: isOpen(state.modal, 'audience-query'),
    deviceCount: query.deviceCount || 0,
    secondsAge,
    count: query.count || 0,
    loadingAudienceQuery: pending(state, 'advertiserAdAudiencesCreate'),
    doneAudiencesQuery: done(state, 'advertiserAdAudiencesCreate'),
    doneDeleteAudiencesQuery: done(state, 'advertiserAdAudiencesDelete'),
    fetching: pending(state, 'advertiserAdAudiencesDetailRead'),
    isPolling: get(state, 'audiences.pollingForQuery'),
    failed: rejected(state, 'advertiserAdAudiencesDetailRead'),
    errorMessage: fromResource.getError(state, 'advertiserAdAudiences'),
    initialValues: {
      query: sql,
      useStandardSql,
    },
  };
};

const mapDispatchToProps = (
  dispatch,
  {
    match: {
      params: { lineItemId },
    },
  }
) => ({
  pollForQueryStatusStart: uuid =>
    dispatch(
      startPollingForQueryStatus({
        params: { lineItemId },
        uuid,
        endpoint: `advertiser-ad/:lineItemId/audiences`,
      })
    ),
  pollForQueryStatusStop: uuid => dispatch(stopPollingForQueryStatus(uuid)),
  hide: () => dispatch(modalHide('audience-query')),
  show: () => dispatch(modalShow('audience-query')),
  deleteAll: () =>
    dispatch(resourceDeleteRequest('advertiser-ad/:id/audiences', { id: lineItemId })),
  verify: () =>
    dispatch(resourceDetailReadRequest('advertiser-ad/:id/audiences/verify', { id: lineItemId })),
  getAudience: () =>
    dispatch(
      resourceDetailReadRequest('advertiser-ad/:id/audiences', {
        id: lineItemId,
      })
    ),
  createAudience: data =>
    dispatch(resourceCreateRequest('advertiser-ad/:id/audiences', { id: lineItemId }, data)),
});

const onSubmit = (values, dispatch, { params, hide, createAudience }) => {
  console.log('sending:', values);
  return createAudience({
    query: values.query,
    options: {
      dryRun: false,
      useStandardSql: values.useStandardSql,
    },
  })
    .then(r => {
      notification.success({ message: 'Success', description: 'Query updated!', duration: 4 });
      hide();
    })
    .catch(e => {
      notification.error({
        message: 'Error',
        description: typeof e === 'string' ? e : 'Could not update query.',
        duration: 0,
      });
    });
};

const validate = createValidator({
  query: [required],
});

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  reduxForm({
    form: 'AudienceTab',
    enableReinitialize: true,
    onSubmit,
    validate,
  })
)(AudienceTabContainer);
